import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslateUnauthenticatedPipe } from '../../filters/translate-unauthenticated.pipe';
import { SiffiLanguagePickerComponent } from '../../components/siffi-language-picker/siffi-language-picker.component';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import AppLanguageService from '../../services/app-language.service';
import ScreeningEscalationRestService from '../../services/screening-escalation-rest.service';
import Config from '../../utils/config.service';
import Session from '../../utils/session.service';
import DictionaryService from '../../services/dictionary.service';

import ScreeningPlatformComponent from './screening-platform.component';

type StepId =
	| 'platform'
	| 'unavailable'
	| 'consent'
	| 'tier1-intro'
	| 'tier1-question'
	| 'tier2-intro'
	| 'phq9'
	| 'gad7'
	| 'crisis'
	| 'result'
	| 'thank-you'
	| 'end'
	| 'auth'
	| 'linkcode'
	| 'linkdone';

@Component({
	standalone: true,
	selector: 'screening-escalation',
	templateUrl: './screening-escalation.component.html',
	imports: [CommonModule, FormsModule, RouterLink, TranslateUnauthenticatedPipe, SiffiLanguagePickerComponent, ScreeningPlatformComponent]
})
export default class ScreeningEscalationComponent implements OnInit, OnDestroy {
	private routing: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private screeningRest: ScreeningEscalationRestService = inject(ScreeningEscalationRestService);
	private appLanguageService: AppLanguageService = inject(AppLanguageService);
	private config: Config = inject(Config);
	private session: Session = inject(Session);
	private dictionaryService: DictionaryService = inject(DictionaryService);
	private readonly draftStorageKeyPrefix: string = 'siffi.pilot.screeningDraft';

	private availabilityTimer: ReturnType<typeof setInterval>;
	private surveyClosesAt: number = 0;
	surveyClosed: boolean = false;
	linkageCodeSaved: boolean = false;
	vm: any = this;
	steps: Array<any> = [
		{ id: 1, label: 'Screening.Pilot.Steps.Consent' },
		{ id: 2, label: 'Screening.Pilot.Steps.Screening' },
		{ id: 3, label: 'Screening.Pilot.Steps.Assessment' },
		{ id: 4, label: 'Screening.Pilot.Steps.Platform' },
		{ id: 5, label: 'Screening.Pilot.Steps.Complete' }
	];
	activeStep: StepId = 'consent';
	activeMacro: number = 1;
	locale: any = 'et_EE';
	localeParams: any = undefined;
	publicLinks: Array<any> = [];
	orgCode: string = 'tln-lpa';
	orgNameKey: string = 'Screening.Pilot.Org.LPA';
	followupOtt: string = '';
	sessionToken: string = '';
	sessionExpiresAt: string = '';
	authSessionToken: string = '';
	textVersion: string = '2026-09';
	errorKey: string = '';
	loading: boolean = false;
	submitting: boolean = false;
	serviceConsent: boolean | null = null;
	researchConsent: boolean | null = null;
	tier1QuestionIndex: number = 0;
	tier2QuestionIndex: number = 0;
	who5: Array<number | null> = [null, null, null, null, null];
	phq2: Array<number | null> = [null, null];
	gad2: Array<number | null> = [null, null];
	phq9: Array<number | null> = [null, null, null, null, null, null, null, null, null];
	gad7: Array<number | null> = [null, null, null, null, null, null, null];
	wpai: number | null = null;
	escalated: boolean = false;
	crisis: boolean = false;
	result: any = null;
	isAuthenticated: boolean = false;
	linkageQuestionId: string = 'pet';
	linkageAnswer: string = '';
	linkageEntryPoint: StepId = 'auth';
	linkageQuestions: Array<any> = [
		{ id: 'pet', label: 'Screening.Pilot.Linkage.Question.Pet' },
		{ id: 'friend', label: 'Screening.Pilot.Linkage.Question.Friend' },
		{ id: 'street', label: 'Screening.Pilot.Linkage.Question.Street' },
		{ id: 'school', label: 'Screening.Pilot.Linkage.Question.School' },
		{ id: 'mother', label: 'Screening.Pilot.Linkage.Question.Mother' }
	];

	who5Items: Array<string> = [
		'Screening.Pilot.WHO5.Q1',
		'Screening.Pilot.WHO5.Q2',
		'Screening.Pilot.WHO5.Q3',
		'Screening.Pilot.WHO5.Q4',
		'Screening.Pilot.WHO5.Q5'
	];
	phq2Items: Array<string> = [
		'Screening.Pilot.PHQ2.Q1',
		'Screening.Pilot.PHQ2.Q2'
	];
	gad2Items: Array<string> = [
		'Screening.Pilot.GAD2.Q1',
		'Screening.Pilot.GAD2.Q2'
	];
	phq9RestItems: Array<string> = [
		'Screening.Pilot.PHQ9.Q3',
		'Screening.Pilot.PHQ9.Q4',
		'Screening.Pilot.PHQ9.Q5',
		'Screening.Pilot.PHQ9.Q6',
		'Screening.Pilot.PHQ9.Q7',
		'Screening.Pilot.PHQ9.Q8',
		'Screening.Pilot.PHQ9.Q9'
	];
	gad7RestItems: Array<string> = [
		'Screening.Pilot.GAD7.Q3',
		'Screening.Pilot.GAD7.Q4',
		'Screening.Pilot.GAD7.Q5',
		'Screening.Pilot.GAD7.Q6',
		'Screening.Pilot.GAD7.Q7'
	];
	who5Options: Array<any> = [5, 4, 3, 2, 1, 0].map((value, index) => ({
		value: value,
		label: 'Screening.Pilot.WHO5.Option.' + index
	}));
	frequencyOptions: Array<any> = [
		{ value: 0, label: 'Screening.Pilot.Frequency.0' },
		{ value: 1, label: 'Screening.Pilot.Frequency.1' },
		{ value: 2, label: 'Screening.Pilot.Frequency.2' },
		{ value: 3, label: 'Screening.Pilot.Frequency.3' }
	];
	tier1Questions: Array<any> = [
		{ key: 'who5', array: 'who5', answerIndex: 0, questionKey: 'Screening.Pilot.WHO5.Q1', guideKey: 'Screening.Pilot.Tier1.Intro.Guide', options: 'who5' },
		{ key: 'who5', array: 'who5', answerIndex: 1, questionKey: 'Screening.Pilot.WHO5.Q2', guideKey: 'Screening.Pilot.Tier1.Intro.Guide', options: 'who5' },
		{ key: 'who5', array: 'who5', answerIndex: 2, questionKey: 'Screening.Pilot.WHO5.Q3', guideKey: 'Screening.Pilot.Tier1.Intro.Guide', options: 'who5' },
		{ key: 'who5', array: 'who5', answerIndex: 3, questionKey: 'Screening.Pilot.WHO5.Q4', guideKey: 'Screening.Pilot.Tier1.Intro.Guide', options: 'who5' },
		{ key: 'who5', array: 'who5', answerIndex: 4, questionKey: 'Screening.Pilot.WHO5.Q5', guideKey: 'Screening.Pilot.Tier1.Intro.Guide', options: 'who5' },
		{ key: 'phq2', array: 'phq2', answerIndex: 0, questionKey: 'Screening.Pilot.PHQ2.Q1', guideKey: 'Screening.Pilot.PHQGAD.Guide', options: 'frequency' },
		{ key: 'phq2', array: 'phq2', answerIndex: 1, questionKey: 'Screening.Pilot.PHQ2.Q2', guideKey: 'Screening.Pilot.PHQGAD.Guide', options: 'frequency' },
		{ key: 'gad2', array: 'gad2', answerIndex: 0, questionKey: 'Screening.Pilot.GAD2.Q1', guideKey: 'Screening.Pilot.PHQGAD.Guide', options: 'frequency' },
		{ key: 'gad2', array: 'gad2', answerIndex: 1, questionKey: 'Screening.Pilot.GAD2.Q2', guideKey: 'Screening.Pilot.PHQGAD.Guide', options: 'frequency' },
		{ key: 'wpai', questionKey: 'Screening.Pilot.WPAI.Question', options: 'slider' }
	];
	tier2Questions: Array<any> = [
		{ array: 'phq9', answerIndex: 2, questionKey: 'Screening.Pilot.PHQ9.Q3' },
		{ array: 'phq9', answerIndex: 3, questionKey: 'Screening.Pilot.PHQ9.Q4' },
		{ array: 'phq9', answerIndex: 4, questionKey: 'Screening.Pilot.PHQ9.Q5' },
		{ array: 'phq9', answerIndex: 5, questionKey: 'Screening.Pilot.PHQ9.Q6' },
		{ array: 'phq9', answerIndex: 6, questionKey: 'Screening.Pilot.PHQ9.Q7' },
		{ array: 'phq9', answerIndex: 7, questionKey: 'Screening.Pilot.PHQ9.Q8' },
		{ array: 'phq9', answerIndex: 8, questionKey: 'Screening.Pilot.PHQ9.Q9' },
		{ array: 'gad7', answerIndex: 2, questionKey: 'Screening.Pilot.GAD7.Q3' },
		{ array: 'gad7', answerIndex: 3, questionKey: 'Screening.Pilot.GAD7.Q4' },
		{ array: 'gad7', answerIndex: 4, questionKey: 'Screening.Pilot.GAD7.Q5' },
		{ array: 'gad7', answerIndex: 5, questionKey: 'Screening.Pilot.GAD7.Q6' },
		{ array: 'gad7', answerIndex: 6, questionKey: 'Screening.Pilot.GAD7.Q7' }
	];
	crisisContacts: Array<any> = [
		{ key: 'Screening.Pilot.Crisis.1', phone: '631 4300' },
		{ key: 'Screening.Pilot.Crisis.2', phone: '655 8088' },
		{ key: 'Screening.Pilot.Crisis.3', phone: '6172 650' },
		{ key: 'Screening.Pilot.Crisis.4', phone: '112' }
	];

	ngOnInit() {
		var queryParams: any = this.routing.getQueryParams() || {};
		this.locale = this.appLanguageService.normalizeLocale(queryParams['locale'] || this.screeningRest.getPilotLocale() || 'et_EE');
		this.localeParams = { locale: this.locale };
		this.initPublicLinks();
		this.orgCode = queryParams['org'] || this.screeningRest.getPilotOrg() || 'tln-lpa';
		this.followupOtt = queryParams['ott'] || '';
		if (queryParams['screeningToken']) {
			this.authSessionToken = queryParams['screeningToken'];
			this.screeningRest.savePilotContext(this.authSessionToken, this.orgCode, this.locale);
		}
		this.orgNameKey = this.orgCode === 'tln-sta' ? 'Screening.Pilot.Org.STA' : 'Screening.Pilot.Org.LPA';
		window.sessionStorage.setItem('locale', this.locale);
		this.screeningRest.savePilotContext(this.screeningRest.getPilotToken(), this.orgCode, this.locale);
		this.isAuthenticated = !!this.session.getSession();
		this.loadAvailability();
	}

	ngOnDestroy() {
		clearInterval(this.availabilityTimer);
	}

	loadAvailability() {
		this.loading = true;
		this.screeningRest.getAvailability().then((response) => {
			var availability: any = response.body;
			this.surveyClosesAt = Date.parse(availability.closesAt);
			this.surveyClosed = !availability.available;
			if (this.surveyClosed || this.closeSurveyIfNeeded()) {
				this.showPlatformAfterClosure();
				return;
			}
			this.loading = false;
			this.activeStep = 'consent';
			this.restoreDraft();
			this.loadAuthState();
			this.ensureSession();
			clearInterval(this.availabilityTimer);
			this.availabilityTimer = setInterval(() => this.closeSurveyIfNeeded(), 1000);
		}).catch(() => {
			this.loading = false;
			this.activateStep('unavailable');
		});
	}

	closeSurveyIfNeeded() {
		if (this.surveyClosed || (this.surveyClosesAt && Date.now() >= this.surveyClosesAt)) {
			this.showPlatformAfterClosure();
			return true;
		}
		return false;
	}

	showPlatformAfterClosure() {
		this.surveyClosed = true;
		this.loading = false;
		this.submitting = false;
		clearInterval(this.availabilityTimer);
		this.clearDraft();
		this.screeningRest.clearPilotContext();
		this.sessionToken = '';
		this.authSessionToken = '';
		this.activateStep('platform');
	}

	refreshAvailabilityAfterError() {
		this.screeningRest.getAvailability().then((response) => {
			if (!response.body.available) {
				this.showPlatformAfterClosure();
			}
		}).catch(() => {});
	}

	continueWithoutAccount() {
		this.clearDraft();
		this.screeningRest.clearPilotContext();
		this.authSessionToken = '';
		this.sessionToken = '';
		this.activateStep('linkdone');
	}

	loadAuthState() {
		this.isAuthenticated = !!this.session.getSession();
	}

	setLocale(locale) {
		this.routing.setQueryParam('locale', this.appLanguageService.normalizeLocale(locale));
		setTimeout(() => window.location.reload(), 200);
	}

	initPublicLinks() {
		this.publicLinks = [
			{
				key: 'Sidebar.Menu.About',
				link: this.config.getSiffiPublicWebUrl(this.locale, '/about-us')
			},
			{
				key: 'Sidebar.Menu.ToS',
				link: this.config.getSiffiPublicWebUrl(this.locale, '/terms-of-service')
			},
			{
				key: 'Sidebar.Menu.PP',
				link: this.config.getSiffiPublicWebUrl(this.locale, '/privacy-policy')
			}
		];
	}

	goToPublicHome(event?) {
		if (event && event.preventDefault) {
			event.preventDefault();
		}
		window.location.href = this.config.getPublicWebHomeUrl('siffi.com', this.locale);
	}

	ensureSession() {
		if (this.followupOtt) {
			this.loading = true;
			this.screeningRest.exchangeFollowupSession(this.followupOtt).then((response) => {
				this.loading = false;
				var body: any = response.body || {};
				this.sessionToken = body.token;
				this.sessionExpiresAt = body.expiresAt;
				this.screeningRest.savePilotContext(this.sessionToken, this.orgCode, this.locale);
			}).catch(() => {
				this.loading = false;
				this.errorKey = 'Screening.Pilot.Error.Session';
				this.refreshAvailabilityAfterError();
			});
			return;
		}
		var existingToken: any = this.screeningRest.getPilotToken();
		if (existingToken) {
			this.sessionToken = existingToken;
			return;
		}
		if (this.authSessionToken && this.shouldRetireAnonymousSession(this.activeStep)) {
			return;
		}
		this.loading = true;
		this.screeningRest.createSession({ campaignCode: this.orgCode }).then((response) => {
			this.loading = false;
			var body: any = response.body || {};
			this.sessionToken = body.token;
			this.sessionExpiresAt = body.expiresAt;
			this.screeningRest.savePilotContext(this.sessionToken, this.orgCode, this.locale);
		}).catch(() => {
			this.loading = false;
			this.errorKey = 'Screening.Pilot.Error.Session';
			this.refreshAvailabilityAfterError();
		});
	}

	ensureSessionToken(): Promise<string> {
		if (this.sessionToken) {
			return Promise.resolve(this.sessionToken);
		}
		var existingToken: any = this.screeningRest.getPilotToken();
		if (existingToken) {
			this.sessionToken = existingToken;
			return Promise.resolve(this.sessionToken);
		}
		if (this.followupOtt) {
			return this.screeningRest.exchangeFollowupSession(this.followupOtt).then((response) => {
				var body: any = response.body || {};
				this.sessionToken = body.token;
				this.sessionExpiresAt = body.expiresAt;
				this.screeningRest.savePilotContext(this.sessionToken, this.orgCode, this.locale);
				return this.sessionToken;
			});
		}
		return this.screeningRest.createSession({ campaignCode: this.orgCode }).then((response) => {
			var body: any = response.body || {};
			this.sessionToken = body.token;
			this.sessionExpiresAt = body.expiresAt;
			this.screeningRest.savePilotContext(this.sessionToken, this.orgCode, this.locale);
			return this.sessionToken;
		});
	}

	createFreshSessionToken(): Promise<string> {
		this.sessionToken = '';
		this.sessionExpiresAt = '';
		this.screeningRest.clearPilotToken();
		return this.screeningRest.createSession({ campaignCode: this.orgCode }).then((response) => {
			var body: any = response.body || {};
			this.sessionToken = body.token;
			this.sessionExpiresAt = body.expiresAt;
			this.screeningRest.savePilotContext(this.sessionToken, this.orgCode, this.locale);
			return this.sessionToken;
		});
	}

	go(step: StepId) {
		if (this.closeSurveyIfNeeded()) {
			return;
		}
		if (this.shouldClaimBeforeContinuing(step)) {
			this.claimOrContinue(step);
			return;
		}
		if (this.shouldRetireAnonymousSession(step)) {
			this.retireAnonymousSession();
		}
		this.activateStep(step);
	}

    shouldScrollToTop(step: StepId) {
        return step !== 'tier1-question' && step !== 'phq9';
    }

	shouldRedirectAuthenticatedUser(step: StepId) {
		return this.isAuthenticated
			&& this.serviceConsent !== false
			&& (step === 'result' || step === 'thank-you' || step === 'auth' || step === 'linkcode' || step === 'linkdone');
	}

	shouldClaimBeforeContinuing(step: StepId) {
		return this.serviceConsent !== false
			&& !!(this.getCurrentAuthSessionToken() || this.sessionToken || this.screeningRest.getPilotToken())
			&& (step === 'result' || step === 'thank-you' || step === 'auth' || step === 'linkcode' || step === 'linkdone');
	}

	shouldRetireAnonymousSession(step: StepId) {
		return !this.isAuthenticated && (step === 'result' || step === 'thank-you' || step === 'auth' || step === 'linkcode' || step === 'linkdone');
	}

	retireAnonymousSession() {
		if (this.sessionToken) {
			this.authSessionToken = this.sessionToken;
		}
		this.sessionToken = '';
		this.sessionExpiresAt = '';
		this.saveCurrentScreeningContext();
		this.saveDraft();
	}

	getCurrentAuthSessionToken() {
		return this.authSessionToken || this.sessionToken;
	}

	redirectAuthenticatedUser() {
		this.errorKey = '';
		this.submitting = true;
		this.claimSessionForAuthenticatedUser().then(() => {
			this.submitting = false;
			this.clearDraft();
			this.screeningRest.clearPilotContext();
			window.location.href = this.config.getPlatformAppUrl();
			// Hash navigation alone keeps the anonymous header mounted.
			window.location.reload();
		}).catch(() => {
			this.submitting = false;
			this.errorKey = 'Screening.Pilot.Error.Session';
			this.refreshAvailabilityAfterError();
		});
	}

	claimOrContinue(step: StepId) {
		this.errorKey = '';
		this.submitting = true;
		this.claimSessionForAuthenticatedUser().then(() => {
			this.submitting = false;
			this.clearDraft();
			this.screeningRest.clearPilotContext();
			window.location.href = this.config.getPlatformAppUrl();
			// Hash navigation alone keeps the anonymous header mounted.
			window.location.reload();
		}).catch((err) => {
			this.submitting = false;
			if (err && err.status === 401 && !this.isAuthenticated) {
				if (this.shouldRetireAnonymousSession(step)) {
					this.retireAnonymousSession();
				}
				this.activateStep(step);
				return;
			}
			this.errorKey = 'Screening.Pilot.Error.Session';
			this.refreshAvailabilityAfterError();
		});
	}

	claimSessionForAuthenticatedUser() {
		var token: string = this.getCurrentAuthSessionToken() || this.screeningRest.getPilotToken();
		if (!token) {
			return Promise.reject();
		}
		return this.screeningRest.claimSession(token);
	}

	activateStep(step: StepId) {
		if (step === 'platform' && !this.surveyClosed && !this.linkageCodeSaved) {
			return;
		}
		if (step === 'platform' && !this.surveyClosed) {
			this.saveCurrentScreeningContext();
		}
		this.activeStep = step;
		this.activeMacro = this.getMacro(step);
		this.errorKey = '';
		if (this.isDraftTerminalStep(step)) {
			this.clearDraft();
			this.screeningRest.clearPilotContext();
		} else {
			this.saveDraft();
		}
		if (this.shouldScrollToTop(step)) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}

	getMacro(step: StepId) {
		switch (step) {
			case 'consent': return 1;
			case 'tier1-intro':
			case 'tier1-question': return 2;
			case 'tier2-intro':
			case 'phq9':
			case 'gad7':
			case 'crisis':
			case 'result': return 3;
			case 'platform':
			case 'auth': return 4;
			case 'linkcode': return 4;
			case 'thank-you': return 5;
			case 'end': return 5;
			case 'linkdone': return 5;
			default: return 1;
		}
	}

	setServiceConsent(value: boolean) {
		this.serviceConsent = value;
		this.errorKey = '';
		this.saveDraft();
	}

	setResearchConsent(value: boolean) {
		this.researchConsent = value;
		this.errorKey = '';
		this.saveDraft();
	}

	getConsentHintKey() {
		if (this.serviceConsent === null || this.researchConsent === null) {
			return 'Screening.Pilot.Consent.Hint.Required';
		}
		if (this.researchConsent && this.serviceConsent) {
			return 'Screening.Pilot.Consent.Hint.ResearchAndService';
		}
		if (this.researchConsent) {
			return 'Screening.Pilot.Consent.Hint.ResearchOnly';
		}
		if (this.serviceConsent) {
			return 'Screening.Pilot.Consent.Hint.ServiceOnly';
		}
		return 'Screening.Pilot.Consent.Hint.None';
	}

	goWho5Next() {
		if (this.who5.some((item) => item === null)) {
			this.errorKey = 'Screening.Pilot.Error.StepRequired';
			window.scrollTo({ top: 0, behavior: 'smooth' });
			return;
		}
		this.tier1QuestionIndex = 5;
		this.go('tier1-question');
	}

	goPhqGadNext() {
		if (this.phq2.some((item) => item === null) || this.gad2.some((item) => item === null)) {
			this.errorKey = 'Screening.Pilot.Error.StepRequired';
			window.scrollTo({ top: 0, behavior: 'smooth' });
			return;
		}
		this.tier1QuestionIndex = 9;
		this.go('tier1-question');
	}

	goPhq9Next() {
		if (this.phq9.some((item) => item === null)) {
			this.errorKey = 'Screening.Pilot.Error.StepRequired';
			window.scrollTo({ top: 0, behavior: 'smooth' });
			return;
		}
		this.go('gad7');
	}

	submitConsents() {
		if (this.closeSurveyIfNeeded()) {
			return;
		}
		if (this.serviceConsent === null || this.researchConsent === null) {
			this.errorKey = 'Screening.Pilot.Error.ConsentRequired';
			return;
		}
		this.submitting = true;
		this.errorKey = '';
		this.ensureSessionToken().then((token) => {
			return this.submitConsentsForToken(token).catch((err) => {
				if (err && err.status === 409) {
					return this.createFreshSessionToken().then((freshToken) => this.submitConsentsForToken(freshToken));
				}
				return Promise.reject(err);
			});
		}).then(() => {
			this.submitting = false;
			if (this.researchConsent) {
				this.tier1QuestionIndex = 0;
				this.go('tier1-intro');
			} else if (this.serviceConsent) {
				this.go('auth');
			} else {
				this.go('end');
			}
		}).catch(() => {
			this.submitting = false;
			this.errorKey = 'Screening.Pilot.Error.ConsentSubmit';
			this.refreshAvailabilityAfterError();
		});
	}

	submitConsentsForToken(token: string) {
		return this.screeningRest.submitConsents(token, {
			service: this.serviceConsent,
			research: this.researchConsent === true,
			textVersion: this.textVersion
		});
	}

	submitTier1() {
		if (this.closeSurveyIfNeeded()) {
			return;
		}
		if (!this.isTier1Complete()) {
			this.errorKey = 'Screening.Pilot.Error.Tier1Required';
			return;
		}
		this.submitting = true;
		this.screeningRest.submitTier1(this.sessionToken, {
			who5: this.who5,
			phq2: this.phq2,
			gad2: this.gad2,
			wpai: this.wpai
		}).then((response) => {
			this.submitting = false;
			var body: any = response.body || {};
			this.escalated = !!body.escalate;
			if (this.escalated) {
				this.phq9[0] = this.phq2[0];
				this.phq9[1] = this.phq2[1];
				this.gad7[0] = this.gad2[0];
				this.gad7[1] = this.gad2[1];
				this.tier2QuestionIndex = 0;
				this.go('tier2-intro');
			} else {
				this.go('thank-you');
			}
		}).catch(() => {
			this.submitting = false;
			this.errorKey = 'Screening.Pilot.Error.Tier1Submit';
			this.refreshAvailabilityAfterError();
		});
	}

	submitTier2() {
		if (this.closeSurveyIfNeeded()) {
			return;
		}
		if (!this.isTier2Complete()) {
			this.errorKey = 'Screening.Pilot.Error.Tier2Required';
			return;
		}
		this.submitting = true;
		this.screeningRest.submitTier2(this.sessionToken, {
			phq9: this.phq9,
			gad7: this.gad7
		}).then((response) => {
			this.submitting = false;
			var body: any = response.body || {};
			this.crisis = !!body.crisis;
			this.result = {
				phq9Total: body.phq9Total,
				gad7Total: body.gad7Total,
				phq9Band: body.phq9Band,
				gad7Band: body.gad7Band,
				who5Total: this.getWho5ResultScore(),
				who5Band: this.getWho5Band()
			};
			if (this.crisis) {
				this.go('crisis');
			} else {
				this.go('result');
			}
		}).catch(() => {
			this.submitting = false;
			this.errorKey = 'Screening.Pilot.Error.Tier2Submit';
			this.refreshAvailabilityAfterError();
		});
	}

	continueFromCrisis() {
		this.go('result');
	}

	goToLogin() {
		if (this.surveyClosed) {
			this.clearDraft();
			this.screeningRest.clearPilotContext();
			this.authSessionToken = '';
			this.sessionToken = '';
		}
		this.saveDraft();
		this.saveCurrentScreeningContext();
		this.routing.setPath('/login', false, false, {
			locale: this.locale,
			screeningToken: this.getCurrentAuthSessionToken(),
			screeningOrg: this.orgCode
		});
	}

	goToRegister() {
		if (this.surveyClosed) {
			this.clearDraft();
			this.screeningRest.clearPilotContext();
			this.authSessionToken = '';
			this.sessionToken = '';
		}
		this.saveDraft();
		this.saveCurrentScreeningContext();
		this.routing.setPath('/register', false, false, {
			locale: this.locale,
			screeningToken: this.getCurrentAuthSessionToken(),
			screeningOrg: this.orgCode
		});
	}

	openLinkageCode(entryPoint: StepId) {
		this.linkageEntryPoint = entryPoint;
		this.errorKey = '';
		this.go('linkcode');
	}

	goBackFromLinkageCode() {
		this.go(this.linkageEntryPoint || 'auth');
	}

	canOfferLinkageCode() {
		return !this.isAuthenticated
			&& this.researchConsent === true
			&& this.getTier1AnsweredCount() === this.getTier1TotalCount();
	}

	isLinkageAnswerValid() {
		return !!(this.linkageQuestionId && (this.linkageAnswer || '').trim());
	}

	submitLinkageCode() {
		if (this.closeSurveyIfNeeded()) {
			return;
		}
		if (!this.isLinkageAnswerValid()) {
			this.errorKey = 'Screening.Pilot.Error.LinkageRequired';
			return;
		}
		var token: string = this.getCurrentAuthSessionToken();
		if (!token) {
			this.errorKey = 'Screening.Pilot.Error.Session';
			return;
		}
		this.submitting = true;
		this.errorKey = '';
		this.screeningRest.submitLinkageCode(token, {
			questionId: this.linkageQuestionId,
			answer: this.linkageAnswer
		}).then(() => {
			this.submitting = false;
			this.linkageAnswer = '';
			this.linkageCodeSaved = true;
			this.clearDraft();
			this.activateStep('platform');
		}).catch(() => {
			this.submitting = false;
			this.errorKey = 'Screening.Pilot.Error.LinkageSubmit';
			this.refreshAvailabilityAfterError();
		});
	}

	getTier1AnsweredCount() {
		return this.who5.filter((item) => item !== null).length
			+ this.phq2.filter((item) => item !== null).length
			+ this.gad2.filter((item) => item !== null).length
			+ (this.wpai === null ? 0 : 1);
	}

	getTier1TotalCount() {
		return 10;
	}

	getWho5ProgressPercent() {
		return (this.who5.filter((item) => item !== null).length / 5) * 100;
	}

	getPhqGadProgressPercent() {
		return ((this.phq2.filter((item) => item !== null).length + this.gad2.filter((item) => item !== null).length) / 4) * 100;
	}

	getPresenteeismProgressPercent() {
		return this.wpai === null ? 75 : 100;
	}

	getTier2AnsweredCount() {
		return this.phq9.filter((item) => item !== null).length + this.gad7.filter((item) => item !== null).length - 4;
	}

	getTier2TotalCount() {
		return 12;
	}

	isTier1Complete() {
		return this.getTier1AnsweredCount() === this.getTier1TotalCount();
	}

	isTier2Complete() {
		return this.phq9.every((item) => item !== null) && this.gad7.every((item) => item !== null);
	}

	startTier1() {
		this.tier1QuestionIndex = 0;
		this.go('tier1-question');
	}

	getCurrentTier1Question() {
		return this.tier1Questions[this.tier1QuestionIndex];
	}

	getCurrentTier1QuestionNumber() {
		return this.tier1QuestionIndex + 1;
	}

	getTier1QuestionProgressPercent() {
		return (this.getCurrentTier1QuestionNumber() / this.tier1Questions.length) * 100;
	}

	getCurrentTier1Options() {
		var question: any = this.getCurrentTier1Question();
		if (!question) {
			return [];
		}
		return question.options === 'who5' ? this.who5Options : this.frequencyOptions;
	}

	getCurrentTier1Answer() {
		var question: any = this.getCurrentTier1Question();
		if (!question) {
			return null;
		}
		if (question.array) {
			return this[question.array][question.answerIndex];
		}
		if (question.key === 'wpai') {
			return this.wpai;
		}
		return null;
	}

	setCurrentTier1Answer(value: number) {
		var question: any = this.getCurrentTier1Question();
		if (!question) {
			return;
		}
		this.errorKey = '';
		if (question.array) {
			this[question.array][question.answerIndex] = value;
			this.saveDraft();
			return;
		}
		if (question.key === 'wpai') {
			this.wpai = value;
			this.saveDraft();
		}
	}

	isCurrentTier1QuestionAnswered() {
		return this.getCurrentTier1Answer() !== null;
	}

	goToPreviousTier1Question() {
		if (this.tier1QuestionIndex === 0) {
			this.go('tier1-intro');
			return;
		}
		this.tier1QuestionIndex--;
		this.errorKey = '';
		this.saveDraft();
	}

	advanceTier1Question() {
		if (!this.isCurrentTier1QuestionAnswered()) {
			this.errorKey = 'Screening.Pilot.Error.Tier1Required';
			return;
		}
		if (this.tier1QuestionIndex === this.tier1Questions.length - 1) {
			this.submitTier1();
			return;
		}
		this.tier1QuestionIndex++;
		this.errorKey = '';
		this.saveDraft();
	}

	isLastTier1Question() {
		return this.tier1QuestionIndex === this.tier1Questions.length - 1;
	}

	startTier2() {
		this.tier2QuestionIndex = 0;
		this.go('phq9');
	}

	getCurrentTier2Question() {
		return this.tier2Questions[this.tier2QuestionIndex];
	}

	getCurrentTier2QuestionNumber() {
		return this.tier2QuestionIndex + 1;
	}

	getTier2QuestionProgressPercent() {
		return (this.getCurrentTier2QuestionNumber() / this.tier2Questions.length) * 100;
	}

	getCurrentTier2Answer() {
		var question: any = this.getCurrentTier2Question();
		if (!question) {
			return null;
		}
		return this[question.array][question.answerIndex];
	}

	setCurrentTier2Answer(value: number) {
		var question: any = this.getCurrentTier2Question();
		if (!question) {
			return;
		}
		this.errorKey = '';
		this[question.array][question.answerIndex] = value;
		this.saveDraft();
	}

	isCurrentTier2QuestionAnswered() {
		return this.getCurrentTier2Answer() !== null;
	}

	goToPreviousTier2Question() {
		if (this.tier2QuestionIndex === 0) {
			this.go('tier2-intro');
			return;
		}
		this.tier2QuestionIndex--;
		this.errorKey = '';
		this.saveDraft();
	}

	advanceTier2Question() {
		if (!this.isCurrentTier2QuestionAnswered()) {
			this.errorKey = 'Screening.Pilot.Error.Tier2Required';
			return;
		}
		if (this.tier2QuestionIndex === this.tier2Questions.length - 1) {
			this.submitTier2();
			return;
		}
		this.tier2QuestionIndex++;
		this.errorKey = '';
		this.saveDraft();
	}

	downloadResults() {
		if (!this.result) {
			return;
		}
		this.createPdfBlob(this.getResultsPdfRows()).then((blob: Blob) => {
			var url: string = window.URL.createObjectURL(blob);
			var link: HTMLAnchorElement = document.createElement('a');
			link.href = url;
			link.download = 'siffi-screening-results.pdf';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		});
	}

	getResultsPdfRows() {
		var phq2Total: number = this.sumAnswers(this.phq2);
		var gad2Total: number = this.sumAnswers(this.gad2);
		var who5Raw: number = this.getWho5RawScore();
		var who5Total: number = this.result.who5Total || this.getWho5ResultScore();
		var phq9Total: number = this.result.phq9Total;
		var gad7Total: number = this.result.gad7Total;
		var phq9Item9: number = this.phq9[8] === null ? 0 : this.phq9[8];
		var escalatedByPhq: boolean = phq2Total >= 2;
		var escalatedByGad: boolean = gad2Total >= 3;
		var rows: Array<any> = [];

		this.addPdfRow(rows, this.pdfText('Title'), 20, true, 10);
		this.addPdfRow(rows, this.pdfText('Generated', { date: new Date().toLocaleString() }), 10, false, 4);
		this.addPdfRow(rows, this.pdfText('Disclaimer'), 11, false, 12);

		this.addPdfRow(rows, this.pdfText('Summary.Title'), 15, true, 6);
		this.addPdfRow(rows, this.pdfText('Summary.WHO5', { score: who5Total, band: this.getWho5BandLabel(this.result.who5Band || this.getWho5Band()) }), 11, false, 2);
		this.addPdfRow(rows, this.pdfText('Summary.PHQ9', { score: phq9Total, band: this.getPhq9BandLabel(this.result.phq9Band) }), 11, false, 2);
		this.addPdfRow(rows, this.pdfText('Summary.GAD7', { score: gad7Total, band: this.getGad7BandLabel(this.result.gad7Band) }), 11, false, 2);
		this.addPdfRow(rows, this.pdfText(phq9Item9 > 0 ? 'Summary.Crisis.Triggered' : 'Summary.Crisis.NotTriggered'), 11, false, 12);

		this.addPdfRow(rows, this.pdfText('Tier1.Title'), 15, true, 6);
		this.addPdfRow(rows, this.pdfText('Tier1.WHO5.Description'), 11, false, 4);
		this.addPdfRow(rows, this.pdfText('Tier1.WHO5.Result', { answers: this.formatAnswers(this.who5), raw: who5Raw, score: who5Total, interpretation: this.getWho5BandDescription(this.result.who5Band || this.getWho5Band()) }), 11, false, 8);
		this.addPdfRow(rows, this.pdfText('Tier1.PHQ2.Description'), 11, false, 4);
		this.addPdfRow(rows, this.pdfText('Tier1.PHQ2.Result', { answers: this.formatAnswers(this.phq2), score: phq2Total, result: this.pdfText(escalatedByPhq ? 'Threshold.Met' : 'Threshold.NotMet') }), 11, false, 8);
		this.addPdfRow(rows, this.pdfText('Tier1.GAD2.Description'), 11, false, 4);
		this.addPdfRow(rows, this.pdfText('Tier1.GAD2.Result', { answers: this.formatAnswers(this.gad2), score: gad2Total, result: this.pdfText(escalatedByGad ? 'Threshold.Met' : 'Threshold.NotMet') }), 11, false, 8);
		this.addPdfRow(rows, this.pdfText('Tier1.WPAI.Description'), 11, false, 4);
		this.addPdfRow(rows, this.pdfText('Tier1.WPAI.Result', { score: this.wpai === null ? this.pdfText('NotAnswered') : this.wpai + ' / 10' }), 11, false, 8);
		this.addPdfRow(rows, this.pdfText('Tier1.Escalation', { result: this.pdfText(this.escalated ? 'Tier1.Escalation.Opened' : 'Tier1.Escalation.NotRequired') }), 11, false, 12);

		this.addPdfRow(rows, this.pdfText('Tier2.Title'), 15, true, 6);
		this.addPdfRow(rows, this.pdfText('Tier2.PHQ9.Description'), 11, false, 4);
		this.addPdfRow(rows, this.pdfText('Tier2.PHQ9.Result', { answers: this.formatAnswers(this.phq9), score: phq9Total, band: this.getPhq9BandLabel(this.result.phq9Band), interpretation: this.getPhq9BandDescription(this.result.phq9Band) }), 11, false, 8);
		this.addPdfRow(rows, this.pdfText('Tier2.PHQ9.Crisis', { score: phq9Item9, result: this.pdfText(phq9Item9 > 0 ? 'Tier2.PHQ9.Crisis.Shown' : 'Tier2.PHQ9.Crisis.NotTriggered') }), 11, false, 8);
		this.addPdfRow(rows, this.pdfText('Tier2.GAD7.Description'), 11, false, 4);
		this.addPdfRow(rows, this.pdfText('Tier2.GAD7.Result', { answers: this.formatAnswers(this.gad7), score: gad7Total, band: this.getGad7BandLabel(this.result.gad7Band), interpretation: this.getGad7BandDescription(this.result.gad7Band) }), 11, false, 12);

		this.addPdfRow(rows, this.pdfText('Meaning.Title'), 15, true, 6);
		this.addPdfRow(rows, this.pdfText('Meaning.Body'), 11, false, 4);
		this.addPdfRow(rows, this.pdfText('Meaning.Support'), 11, false, 0);
		return rows;
	}

	pdfText(key: string, params?: any) {
		var value: string = this.dictionaryService.translate('Screening.Pilot.ResultsPdf.' + key, this.locale);
		if (params) {
			Object.keys(params).forEach((paramKey) => {
				value = value.replace(new RegExp('\\{' + paramKey + '\\}', 'g'), params[paramKey]);
			});
		}
		return value;
	}

	addPdfRow(rows: Array<any>, text: string, size?: number, bold?: boolean, spaceAfter?: number) {
		rows.push({
			text: text,
			size: size || 11,
			bold: bold === true,
			spaceAfter: spaceAfter || 0
		});
	}

	createPdfBlob(rows: Array<any>): Promise<Blob> {
		var pageWidth: number = 595.28;
		var pageHeight: number = 841.89;
		var margin: number = 46;
		var canvases: Array<HTMLCanvasElement> = this.renderPdfRowsToCanvases(rows, pageWidth, pageHeight, margin);
		return Promise.all(canvases.map((canvas) => this.canvasToJpegPage(canvas))).then((pages) => {
			return this.buildImagePdfBlob(pages, pageWidth, pageHeight);
		});
	}

	renderPdfRowsToCanvases(rows: Array<any>, pageWidth: number, pageHeight: number, margin: number) {
		var scale: number = 2;
		var pages: Array<HTMLCanvasElement> = [];
		var canvas: HTMLCanvasElement;
		var context: CanvasRenderingContext2D;
		var y: number;
		var createPage = () => {
			canvas = document.createElement('canvas');
			canvas.width = Math.ceil(pageWidth * scale);
			canvas.height = Math.ceil(pageHeight * scale);
			context = canvas.getContext('2d') as CanvasRenderingContext2D;
			context.scale(scale, scale);
			context.fillStyle = '#ffffff';
			context.fillRect(0, 0, pageWidth, pageHeight);
			context.fillStyle = '#1f2933';
			context.textBaseline = 'alphabetic';
			y = margin;
			pages.push(canvas);
		};

		createPage();

		for (var i = 0; i < rows.length; i++) {
			var row: any = rows[i];
			context.font = this.getPdfCanvasFont(row);
			var wrapped: Array<string> = this.wrapCanvasText(context, this.normalizePdfText(row.text), pageWidth - (margin * 2));
			for (var j = 0; j < wrapped.length; j++) {
				var lineHeight: number = Math.max(13, row.size + 5);
				if (y + lineHeight > pageHeight - margin) {
					createPage();
					context.font = this.getPdfCanvasFont(row);
				}
				context.fillText(wrapped[j], margin, y + row.size);
				y += lineHeight;
			}
			y += row.spaceAfter || 0;
		}
		return pages;
	}

	getPdfCanvasFont(row: any) {
		return (row.bold ? '700 ' : '400 ') + row.size + 'px Helvetica, Arial, sans-serif';
	}

	wrapCanvasText(context: CanvasRenderingContext2D, text: string, maxWidth: number) {
		var words: Array<string> = text.split(/\s+/);
		var lines: Array<string> = [];
		var line: string = '';
		for (var i = 0; i < words.length; i++) {
			var word: string = words[i];
			var testLine: string = line ? line + ' ' + word : word;
			if (context.measureText(testLine).width <= maxWidth) {
				line = testLine;
				continue;
			}
			if (line) {
				lines.push(line);
			}
			if (context.measureText(word).width <= maxWidth) {
				line = word;
				continue;
			}
			var broken: Array<string> = this.breakCanvasWord(context, word, maxWidth);
			for (var j = 0; j < broken.length - 1; j++) {
				lines.push(broken[j]);
			}
			line = broken[broken.length - 1] || '';
		}
		if (line) {
			lines.push(line);
		}
		return lines.length ? lines : [''];
	}

	breakCanvasWord(context: CanvasRenderingContext2D, word: string, maxWidth: number) {
		var parts: Array<string> = [];
		var part: string = '';
		for (var i = 0; i < word.length; i++) {
			var testPart: string = part + word.charAt(i);
			if (part && context.measureText(testPart).width > maxWidth) {
				parts.push(part);
				part = word.charAt(i);
			} else {
				part = testPart;
			}
		}
		if (part) {
			parts.push(part);
		}
		return parts;
	}

	canvasToJpegPage(canvas: HTMLCanvasElement): Promise<any> {
		return new Promise((resolve) => {
			canvas.toBlob((blob: Blob | null) => {
				if (!blob) {
					resolve({
						width: canvas.width,
						height: canvas.height,
						data: this.dataUrlToBytes(canvas.toDataURL('image/jpeg', 0.92))
					});
					return;
				}
				var reader: FileReader = new FileReader();
				reader.onload = () => {
					resolve({
						width: canvas.width,
						height: canvas.height,
						data: new Uint8Array(reader.result as ArrayBuffer)
					});
				};
				reader.readAsArrayBuffer(blob);
			}, 'image/jpeg', 0.92);
		});
	}

	dataUrlToBytes(dataUrl: string) {
		var base64: string = dataUrl.split(',')[1] || '';
		var binary: string = window.atob(base64);
		var bytes: Uint8Array = new Uint8Array(binary.length);
		for (var i = 0; i < binary.length; i++) {
			bytes[i] = binary.charCodeAt(i);
		}
		return bytes;
	}

	buildImagePdfBlob(pages: Array<any>, pageWidth: number, pageHeight: number) {
		var parts: Array<any> = [];
		var offsets: Array<number> = [0];
		var byteLength: number = 0;
		var pageKids: Array<string> = [];
		var appendString = (text: string) => {
			parts.push(text);
			byteLength += text.length;
		};
		var appendBytes = (bytes: Uint8Array) => {
			parts.push(bytes);
			byteLength += bytes.length;
		};
		var appendObject = (objectId: number, content: string) => {
			offsets[objectId] = byteLength;
			appendString(objectId + ' 0 obj\n' + content + '\nendobj\n');
		};

		appendString('%PDF-1.4\n');
		appendObject(1, '<< /Type /Catalog /Pages 2 0 R >>');

		for (var i = 0; i < pages.length; i++) {
			var pageObjectId: number = 3 + (i * 3);
			var contentObjectId: number = pageObjectId + 1;
			var imageObjectId: number = pageObjectId + 2;
			var imageName: string = 'Im' + (i + 1);
			var content: string = 'q\n' + pageWidth + ' 0 0 ' + pageHeight + ' 0 0 cm\n/' + imageName + ' Do\nQ';
			pageKids.push(pageObjectId + ' 0 R');
			appendObject(pageObjectId, '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' + pageWidth + ' ' + pageHeight + '] /Resources << /XObject << /' + imageName + ' ' + imageObjectId + ' 0 R >> >> /Contents ' + contentObjectId + ' 0 R >>');
			appendObject(contentObjectId, '<< /Length ' + content.length + ' >>\nstream\n' + content + '\nendstream');
			offsets[imageObjectId] = byteLength;
			appendString(imageObjectId + ' 0 obj\n<< /Type /XObject /Subtype /Image /Width ' + pages[i].width + ' /Height ' + pages[i].height + ' /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ' + pages[i].data.length + ' >>\nstream\n');
			appendBytes(pages[i].data);
			appendString('\nendstream\nendobj\n');
		}

		appendObject(2, '<< /Type /Pages /Kids [' + pageKids.join(' ') + '] /Count ' + pages.length + ' >>');
		var xrefStart: number = byteLength;
		var objectCount: number = 2 + (pages.length * 3);
		appendString('xref\n0 ' + (objectCount + 1) + '\n');
		appendString('0000000000 65535 f \n');
		for (var x = 1; x <= objectCount; x++) {
			appendString(this.padPdfOffset(offsets[x]) + ' 00000 n \n');
		}
		appendString('trailer\n<< /Size ' + (objectCount + 1) + ' /Root 1 0 R >>\nstartxref\n' + xrefStart + '\n%%EOF');
		return new Blob(parts, { type: 'application/pdf' });
	}

	wrapPdfText(text: string, size: number, maxWidth: number) {
		var sanitized: string = this.normalizePdfText(text);
		var maxChars: number = Math.max(42, Math.floor(maxWidth / (size * 0.52)));
		var words: Array<string> = sanitized.split(/\s+/);
		var lines: Array<string> = [];
		var line: string = '';
		for (var i = 0; i < words.length; i++) {
			var testLine: string = line ? line + ' ' + words[i] : words[i];
			if (testLine.length > maxChars && line) {
				lines.push(line);
				line = words[i];
			} else {
				line = testLine;
			}
		}
		if (line) {
			lines.push(line);
		}
		return lines.length ? lines : [''];
	}

	escapePdfText(text: string) {
		return this.normalizePdfText(text).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
	}

	normalizePdfText(text: string) {
		return (text || '')
			.replace(/[–—]/g, '-')
			.replace(/[“”]/g, '"')
			.replace(/[‘’]/g, "'")
			.replace(/≥/g, '>=')
			.replace(/≤/g, '<=')
			.replace(/×/g, 'x');
	}

	padPdfOffset(value: number) {
		var text: string = value.toString();
		while (text.length < 10) {
			text = '0' + text;
		}
		return text;
	}

	sumAnswers(answers: Array<number | null>) {
		return answers.reduce((sum, item) => sum + (item === null ? 0 : item), 0);
	}

	formatAnswers(answers: Array<number | null>) {
		return answers.map((item) => item === null ? '-' : item).join(', ');
	}

	getWho5BandLabel(band: string) {
		switch (band) {
			case 'severe_low': return this.pdfText('WHO5.Band.SevereLow');
			case 'low': return this.pdfText('WHO5.Band.Low');
			case 'good': return this.pdfText('WHO5.Band.Good');
			default: return band || this.pdfText('Unknown');
		}
	}

	getWho5BandDescription(band: string) {
		switch (band) {
			case 'severe_low': return this.pdfText('WHO5.Description.SevereLow');
			case 'low': return this.pdfText('WHO5.Description.Low');
			case 'good': return this.pdfText('WHO5.Description.Good');
			default: return this.pdfText('WHO5.Description.Unknown');
		}
	}

	getPhq9BandLabel(band: string) {
		switch (band) {
			case 'minimal': return this.pdfText('Band.Minimal');
			case 'mild': return this.pdfText('Band.Mild');
			case 'moderate': return this.pdfText('Band.Moderate');
			case 'moderately-severe': return this.pdfText('Band.ModeratelySevere');
			case 'severe': return this.pdfText('Band.Severe');
			default: return band || this.pdfText('Unknown');
		}
	}

	getPhq9BandDescription(band: string) {
		switch (band) {
			case 'minimal': return this.pdfText('PHQ9.Description.Minimal');
			case 'mild': return this.pdfText('PHQ9.Description.Mild');
			case 'moderate': return this.pdfText('PHQ9.Description.Moderate');
			case 'moderately-severe': return this.pdfText('PHQ9.Description.ModeratelySevere');
			case 'severe': return this.pdfText('PHQ9.Description.Severe');
			default: return this.pdfText('PHQ9.Description.Unknown');
		}
	}

	getGad7BandLabel(band: string) {
		switch (band) {
			case 'minimal': return this.pdfText('Band.Minimal');
			case 'mild': return this.pdfText('Band.Mild');
			case 'moderate': return this.pdfText('Band.Moderate');
			case 'severe': return this.pdfText('Band.Severe');
			default: return band || this.pdfText('Unknown');
		}
	}

	getGad7BandDescription(band: string) {
		switch (band) {
			case 'minimal': return this.pdfText('GAD7.Description.Minimal');
			case 'mild': return this.pdfText('GAD7.Description.Mild');
			case 'moderate': return this.pdfText('GAD7.Description.Moderate');
			case 'severe': return this.pdfText('GAD7.Description.Severe');
			default: return this.pdfText('GAD7.Description.Unknown');
		}
	}

	saveDraft() {
		if (this.isDraftTerminalStep(this.activeStep)) {
			this.clearDraft();
			return;
		}
		var draftStorageKey: string = this.getDraftStorageKey();
		if (!draftStorageKey) {
			return;
		}
		if (this.activeStep === 'platform') {
			// Keep only the destination and claim token, never completed survey answers.
			window.sessionStorage.setItem(draftStorageKey, JSON.stringify({
				orgCode: this.orgCode,
				textVersion: this.textVersion,
				tokenFingerprint: this.getDraftToken(),
				activeStep: 'platform',
				linkageCodeSaved: this.linkageCodeSaved,
				activeMacro: this.getMacro('platform')
			}));
			return;
		}
		var draft: any = {
			orgCode: this.orgCode,
			locale: this.locale,
			textVersion: this.textVersion,
			tokenFingerprint: this.getDraftToken(),
			activeStep: this.activeStep,
			activeMacro: this.activeMacro,
			serviceConsent: this.serviceConsent,
			researchConsent: this.researchConsent,
			tier1QuestionIndex: this.tier1QuestionIndex,
			tier2QuestionIndex: this.tier2QuestionIndex,
			who5: this.who5,
			phq2: this.phq2,
			gad2: this.gad2,
			phq9: this.phq9,
			gad7: this.gad7,
			wpai: this.wpai,
			escalated: this.escalated,
			crisis: this.crisis,
			result: this.result,
			linkageEntryPoint: this.linkageEntryPoint
		};
		window.sessionStorage.setItem(draftStorageKey, JSON.stringify(draft));
	}

	restoreDraft() {
		this.clearLegacyDraft();
		var draftStorageKey: string = this.getDraftStorageKey();
		if (!draftStorageKey) {
			return;
		}
		var rawDraft: string = window.sessionStorage.getItem(draftStorageKey);
		if (!rawDraft) {
			return;
		}
		try {
			var draft: any = JSON.parse(rawDraft);
			if (!draft
				|| draft.orgCode !== this.orgCode
				|| draft.textVersion !== this.textVersion
				|| draft.tokenFingerprint !== this.getDraftToken()) {
				window.sessionStorage.removeItem(draftStorageKey);
				return;
			}
			if (draft.activeStep === 'platform' && draft.linkageCodeSaved !== true) {
				window.sessionStorage.removeItem(draftStorageKey);
				return;
			}
			this.linkageCodeSaved = draft.linkageCodeSaved === true;
			this.activeStep = draft.activeStep || this.activeStep;
			this.activeMacro = this.getMacro(this.activeStep);
			this.serviceConsent = typeof draft.serviceConsent === 'boolean' ? draft.serviceConsent : this.serviceConsent;
			this.researchConsent = typeof draft.researchConsent === 'boolean' ? draft.researchConsent : this.researchConsent;
			this.tier1QuestionIndex = typeof draft.tier1QuestionIndex === 'number' ? draft.tier1QuestionIndex : this.tier1QuestionIndex;
			this.tier2QuestionIndex = typeof draft.tier2QuestionIndex === 'number' ? draft.tier2QuestionIndex : this.tier2QuestionIndex;
			this.who5 = Array.isArray(draft.who5) ? draft.who5 : this.who5;
			this.phq2 = Array.isArray(draft.phq2) ? draft.phq2 : this.phq2;
			this.gad2 = Array.isArray(draft.gad2) ? draft.gad2 : this.gad2;
			this.phq9 = Array.isArray(draft.phq9) ? draft.phq9 : this.phq9;
			this.gad7 = Array.isArray(draft.gad7) ? draft.gad7 : this.gad7;
			this.wpai = typeof draft.wpai === 'number' ? draft.wpai : this.wpai;
			this.escalated = !!draft.escalated;
			this.crisis = !!draft.crisis;
			this.result = draft.result || this.result;
			this.linkageEntryPoint = draft.linkageEntryPoint || this.linkageEntryPoint;
		} catch (e) {
			window.sessionStorage.removeItem(draftStorageKey);
		}
	}

	clearDraft() {
		var draftStorageKey: string = this.getDraftStorageKey();
		if (draftStorageKey) {
			window.sessionStorage.removeItem(draftStorageKey);
		}
		this.clearLegacyDraft();
	}

	private getDraftStorageKey() {
		var token: string = this.getDraftToken();
		if (!token) {
			return '';
		}
		return this.draftStorageKeyPrefix + '.' + this.orgCode + '.' + token;
	}

	private getDraftToken() {
		return this.getCurrentAuthSessionToken() || this.screeningRest.getPilotToken() || this.followupOtt || '';
	}

	private saveCurrentScreeningContext() {
		var token: string = this.getCurrentAuthSessionToken();
		if (token) {
			this.screeningRest.savePilotContext(token, this.orgCode, this.locale);
		}
	}

	private isDraftTerminalStep(step: StepId) {
		return step === 'thank-you' || step === 'end' || step === 'linkdone';
	}

	private clearLegacyDraft() {
		window.sessionStorage.removeItem(this.draftStorageKeyPrefix);
	}

	isLastTier2Question() {
		return this.tier2QuestionIndex === this.tier2Questions.length - 1;
	}

	getTier2StageCounter() {
		return this.tier2QuestionIndex < 7 ? '1 / 2' : '2 / 2';
	}

	getWho5RawScore() {
		return this.who5.reduce((sum, item) => sum + (item === null ? 0 : item), 0);
	}

	getWho5ResultScore() {
		return this.getWho5RawScore() * 4;
	}

	getWho5Band() {
		var score: number = this.getWho5ResultScore();
		if (score <= 28) {
			return 'severe_low';
		}
		if (score <= 50) {
			return 'low';
		}
		return 'good';
	}

	trackByIndex(index) {
		return index;
	}
}
